import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star, Sparkles, Target } from "lucide-react";
import { Reveal, SectionTitle, Overline } from "@/components/Reveal";

const TOTAL = 25;

function pickHighlights() {
  const count = Math.floor(Math.random() * 4) + 3; // 3..6
  const set = new Set();
  while (set.size < count) set.add(Math.floor(Math.random() * TOTAL));
  return set;
}

export const OpportunityGenerator = () => {
  const [active, setActive] = useState(() => pickHighlights());
  const [round, setRound] = useState(0);
  const [locked, setLocked] = useState(false);
  const [ripples, setRipples] = useState([]);
  const rippleId = useRef(0);

  const generate = useCallback(
    (e) => {
      if (locked) return;
      if (e) {
        const rect = e.currentTarget.getBoundingClientRect();
        const id = rippleId.current++;
        setRipples((r) => [...r, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
        setTimeout(() => setRipples((r) => r.filter((x) => x.id !== id)), 650);
      }
      setLocked(true);
      setActive(pickHighlights());
      setRound((n) => n + 1);
      setTimeout(() => setLocked(false), 1000);
    },
    [locked],
  );

  useEffect(() => {}, []);

  return (
    <section id="oportunidades" className="relative py-20 md:py-28" data-testid="opportunity-section">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] radial-gold blur-3xl opacity-50 pointer-events-none" />
      <div className="max-w-3xl mx-auto px-5 md:px-8 relative text-center">
        <Reveal>
          <Overline className="inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Ferramenta
          </Overline>
          <SectionTitle className="mt-3">
            Gerador de <span className="text-gold-gradient">Oportunidades</span>
          </SectionTitle>
          <p className="mt-4 text-white/55 max-w-xl mx-auto font-body">
            Gosta de jogar e quer tentar ganhar no Mines? Em vez de gastar com robôs de Telegram, use o nosso gerador automático grátis com sinais do Mines. Ele se baseia no algoritmo do melhor jogo de Minas para gerar jogadas como um robô Mines.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 flex justify-center">
            <div
              className="grid grid-cols-5 gap-2.5 sm:gap-3 w-full max-w-md p-4 sm:p-5 glass-gold"
              data-testid="opportunity-board"
            >
              {Array.from({ length: TOTAL }).map((_, i) => {
                const isActive = active.has(i);
                return (
                  <motion.div
                    key={`${round}-${i}`}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: (i % 5) * 0.03 + Math.floor(i / 5) * 0.03, ease: [0.16, 1, 0.3, 1] }}
                    whileHover={{ scale: 1.06 }}
                    className="relative aspect-square rounded-xl flex items-center justify-center overflow-hidden"
                    style={
                      isActive
                        ? {
                            background: "linear-gradient(135deg,#FFD84D,#F5C542,#F28C28)",
                            boxShadow: "0 0 18px rgba(245, 197, 66,0.55)",
                          }
                        : {
                            background: "linear-gradient(135deg,#16233f,#1c2f52)",
                            boxShadow: "inset 0 0 12px rgba(0,0,0,0.4)",
                            border: "1px solid rgba(255,255,255,0.05)",
                          }
                    }
                    data-testid={isActive ? `opp-tile-active-${i}` : `opp-tile-${i}`}
                  >
                    {isActive ? (
                      <motion.div
                        initial={{ scale: 0, rotate: -30 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 14, delay: 0.15 }}
                      >
                        <Star className="w-1/2 h-1/2 min-w-6 text-white drop-shadow" fill="white" />
                      </motion.div>
                    ) : (
                      <span className="w-1/2 h-1/2 rounded-full bg-white/[0.06]" />
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <button
            onClick={generate}
            disabled={locked}
            className="relative overflow-hidden mt-10 btn-gold font-display font-bold text-base md:text-lg px-10 py-4 rounded-full hover:scale-105 transition-transform duration-300 disabled:opacity-70 disabled:hover:scale-100 disabled:cursor-not-allowed"
            data-testid="opportunity-generate-btn"
          >
            <span className="relative z-10 inline-flex items-center gap-2">
              {!locked && <Target className="w-4 h-4" />}
              {locked ? "Gerando..." : "GERAR OPORTUNIDADE"}
            </span>
            {ripples.map((r) => (
              <span
                key={r.id}
                className="absolute rounded-full bg-white/40 pointer-events-none"
                style={{
                  left: r.x,
                  top: r.y,
                  width: 8,
                  height: 8,
                  transform: "translate(-50%,-50%)",
                  animation: "oppRipple 0.65s ease-out forwards",
                }}
              />
            ))}
          </button>
        </Reveal>
      </div>
    </section>
  );
};
